import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAssessmentQueue, GetAssessmentQueueOutputType } from 'zite-endpoints-sdk';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { ClipboardCheck, Search, User, BookOpen, ExternalLink, AlertTriangle, Pause, RotateCcw, Camera, Loader2, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

type Data = GetAssessmentQueueOutputType;
type Session = Data['sessions'][0];

const STATUS_STYLES: Record<string, { label: string; cls: string }> = {
  'In Progress': { label: 'In Progress', cls: 'bg-primary/10 text-primary border-primary/30' },
  'Paused': { label: 'Paused', cls: 'bg-orange-500/10 text-orange-600 border-orange-300' },
  'Further Training': { label: 'Further Training', cls: 'bg-destructive/10 text-destructive border-destructive/30' },
  'Approved': { label: 'Approved', cls: 'bg-green-500/10 text-green-700 border-green-300' },
};

function CountPill({ label, count, active, onClick }: { label: string; count: number; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all whitespace-nowrap ${
        active ? 'bg-primary text-primary-foreground border-primary' : 'bg-card text-muted-foreground border-border hover:border-primary/40'
      }`}
    >
      {label} <span className="ml-1 font-bold">{count}</span>
    </button>
  );
}

function SessionCard({ session, onOpen }: { session: Session; onOpen: () => void }) {
  const style = STATUS_STYLES[session.status] || { label: session.status, cls: 'bg-muted text-muted-foreground border-border' };
  const dateStr = session.startedAt ? format(new Date(session.startedAt), 'd MMM yyyy, h:mm a') : session.createdAt ? format(new Date(session.createdAt), 'd MMM yyyy') : '—';
  const isReassessment = !!session.parentSessionId;
  const canOpen = session.status === 'In Progress' || session.status === 'Paused' || session.status === 'Further Training';

  return (
    <div className="border border-border rounded-lg bg-card overflow-hidden">
      <div className="p-4 space-y-3">
        {/* Top row: employee + status */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <User className="w-4 h-4 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground truncate">{session.employeeName || 'Unknown'}</p>
              <p className="text-xs text-muted-foreground truncate">{session.courseTitle}{session.courseCode ? ` (${session.courseCode})` : ''}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {session.isOverdue && <Badge variant="outline" className="text-[10px] bg-destructive/10 text-destructive border-destructive/30">Overdue</Badge>}
            <Badge variant="outline" className={`text-[10px] ${style.cls}`}>{style.label}</Badge>
          </div>
        </div>

        {/* Module + reference */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><BookOpen className="w-3 h-3" /> {session.moduleLabel || 'Module'}</span>
          <span className="font-mono text-[11px]">{session.sessionReference}</span>
          {session.evidenceCount > 0 && (
            <span className="flex items-center gap-1"><Camera className="w-3 h-3" /> {session.evidenceCount} evidence</span>
          )}
        </div>

        {/* Parent link for reassessments */}
        {isReassessment && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/50 rounded px-2.5 py-1.5">
            <Link2 className="w-3 h-3 text-primary shrink-0" />
            <span>Reassessment of <span className="font-mono font-medium text-foreground">{session.parentSessionReference || session.parentSessionId.slice(0, 8)}</span></span>
            {session.attemptNumber > 1 && <Badge variant="outline" className="text-[10px] ml-1">Attempt {session.attemptNumber}</Badge>}
          </div>
        )}

        {/* Footer: date + action */}
        <div className="flex items-center justify-between pt-1">
          <span className="text-[11px] text-muted-foreground">
            {session.status === 'Paused' ? <span className="flex items-center gap-1"><Pause className="w-3 h-3" /> Paused — {dateStr}</span> : `Started ${dateStr}`}
          </span>
          {canOpen ? (
            <Button size="sm" className="h-7 text-xs gap-1" onClick={onOpen}>
              <ExternalLink className="w-3 h-3" />
              {session.status === 'Paused' ? 'Resume' : session.status === 'Further Training' ? 'Review' : 'Open'}
            </Button>
          ) : (
            <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={onOpen}>
              <ExternalLink className="w-3 h-3" /> View
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PracticalAssessmentsSection() {
  const navigate = useNavigate();
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [courseFilter, setCourseFilter] = useState('all');
  const [search, setSearch] = useState('');

  const load = () => {
    setLoading(true);
    setError('');
    const statusMap: Record<string, string> = {
      all: 'all', in_progress: 'in_progress', paused: 'paused',
      further_training: 'further_training', reassessment: 'reassessment',
      completed_by_me: 'completed_by_me',
    };
    getAssessmentQueue({
      statusFilter: (statusMap[statusFilter] || 'all') as any,
      courseFilter: courseFilter !== 'all' ? courseFilter : undefined,
      searchQuery: search || undefined,
    })
      .then(setData)
      .catch(e => { setError(e?.message || 'Failed to load'); toast.error('Failed to load assessment queue'); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [statusFilter, courseFilter]);
  useEffect(() => {
    const t = setTimeout(load, 400);
    return () => clearTimeout(t);
  }, [search]);

  if (loading && !data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-72" />
        <div className="flex gap-2"><Skeleton className="h-8 w-20" /><Skeleton className="h-8 w-20" /><Skeleton className="h-8 w-20" /></div>
        {[1, 2, 3].map(i => <Skeleton key={i} className="h-32" />)}
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="py-16 text-center">
        <AlertTriangle className="w-10 h-10 text-destructive mx-auto mb-3" />
        <p className="text-sm text-destructive font-medium mb-2">Failed to load assessment queue</p>
        <p className="text-xs text-muted-foreground mb-4">{error}</p>
        <Button variant="outline" size="sm" onClick={load}>Retry</Button>
      </div>
    );
  }

  const counts = data?.counts || { all: 0, inProgress: 0, paused: 0, furtherTraining: 0, overdue: 0, completedByMe: 0, new: 0 };
  const sessions = data?.sessions || [];
  const courses = data?.courses || [];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-primary" />
            Practical Assessments
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            {counts.all} active session{counts.all !== 1 ? 's' : ''} awaiting assessment
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} className="gap-1.5">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
          Refresh
        </Button>
      </div>

      {/* Status filter pills */}
      <div className="flex gap-2 flex-wrap">
        <CountPill label="All Active" count={counts.all} active={statusFilter === 'all'} onClick={() => setStatusFilter('all')} />
        <CountPill label="In Progress" count={counts.inProgress} active={statusFilter === 'in_progress'} onClick={() => setStatusFilter('in_progress')} />
        <CountPill label="Paused" count={counts.paused} active={statusFilter === 'paused'} onClick={() => setStatusFilter('paused')} />
        <CountPill label="Further Training" count={counts.furtherTraining} active={statusFilter === 'further_training'} onClick={() => setStatusFilter('further_training')} />
        <CountPill label="Completed by Me" count={counts.completedByMe} active={statusFilter === 'completed_by_me'} onClick={() => setStatusFilter('completed_by_me')} />
      </div>

      {/* Search + course filter */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative w-56">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input placeholder="Search employee, course, module…" value={search} onChange={e => setSearch(e.target.value)} className="pl-8 h-9 text-sm" />
        </div>
        {courses.length > 0 && (
          <Select value={courseFilter} onValueChange={setCourseFilter}>
            <SelectTrigger className="w-52 h-9 text-xs"><SelectValue placeholder="All courses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Courses</SelectItem>
              {courses.map(c => <SelectItem key={c.id} value={c.id}>{c.title}{c.courseCode ? ` (${c.courseCode})` : ''}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Session cards */}
      {sessions.length === 0 ? (
        <div className="py-16 text-center">
          <ClipboardCheck className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            {counts.all === 0 ? 'No assessment sessions found. Sessions are created when a reassessment is requested or an assessment is started.' : 'No sessions match your current filters.'}
          </p>
        </div>
      ) : (
        <div className="grid gap-3">
          {sessions.map(s => (
            <SessionCard
              key={s.sessionId}
              session={s}
              onOpen={() => navigate(`/train/assess/${s.sessionId}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
